import { Box, Stack, styled, Typography } from '@mui/material';
import {
  BaseEdge,
  Edge,
  EdgeLabelRenderer,
  EdgeProps,
  getBezierPath,
  getSmoothStepPath,
} from '@xyflow/react';
import { StatusWorkflowActionType } from '../../../services/describeWorkflowUtils';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useTranslation } from 'react-i18next';
import { WorkflowActionType } from '../../../data/models/workflow.model';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { memo } from 'react';

const PREFIX = 'WorkflowStepEdge';

const classes = {
  container: `${PREFIX}-container`,
  succeedLabel: `${PREFIX}-succeedLabel`,
  failLabel: `${PREFIX}-failLabel`,
  cancelLabel: `${PREFIX}-cancelLabel`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`& .${classes.container}`]: {
    position: 'absolute',
    backgroundColor: 'white',
    gap: 5,
  },
  [`& .${classes.succeedLabel}`]: {
    color: 'green',
  },
  [`& .${classes.failLabel}`]: {
    color: 'red',
  },
  [`& .${classes.cancelLabel}`]: {
    color: 'orange',
  },
}));

export enum EdgeType {
  Bezier = 'bezier',
  Step = 'step',
}

type CustomEdgeData = {
  status?: StatusWorkflowActionType;
  edgeType?: EdgeType;
};

type CustomEdge = Edge<CustomEdgeData, 'custom'>;

const WorkflowStepEdge = ({
  id,
  sourceX,
  sourceY,
  sourcePosition,
  targetX,
  targetY,
  targetPosition,
  data,
}: EdgeProps<CustomEdge>) => {
  const { t } = useTranslation('common');
  const stepParams = {
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  };
  const [edgePath] =
    data?.edgeType === EdgeType.Step
      ? getSmoothStepPath(stepParams)
      : getBezierPath(stepParams);
  const label = data?.status ? t(`workflows.${data?.status}`) : undefined;

  const labelClassName = data?.status ? classes[`${data?.status}Label`] : '';
  let icon;
  switch (data?.status) {
    case WorkflowActionType.Succeed:
      icon = <CheckCircleOutlineIcon />;
      break;
    case WorkflowActionType.Fail:
      icon = <ErrorOutlineIcon />;
      break;
    case WorkflowActionType.Cancel:
      icon = <HighlightOffIcon />;
      break;
  }

  return (
    <>
      <BaseEdge id={id} path={edgePath} />
      <EdgeLabelRenderer>
        <StyledBox>
          <Stack
            direction="row"
            className={`${classes.container} ${labelClassName}`}
            sx={{
              transform: `translate(-50%, 0%) translate(${sourceX}px,${sourceY + 10}px)`,
            }}>
            {icon}
            <Typography variant="body2">{label}</Typography>
          </Stack>
        </StyledBox>
      </EdgeLabelRenderer>
    </>
  );
};

export default memo(WorkflowStepEdge);
